// Kalender (README §5.7, Screen 40) – Einträge je Tag, Filter nach Art, Fristen als virtuelle Einträge.

import type { FristEintrag } from '../kalender/fristen';
import { terminKategorie, terminTag, type TerminKategorie } from './termine';

export type KalenderFilter = 'alle' | TerminKategorie;

export interface KalenderTerminRoh {
  id: string;
  titel: string | null;
  typ: string | null;
  typ_freitext?: string | null;
  start_datum: string;
  ganztaegig?: boolean | null;
  status?: string | null;
  objekt_id?: string | null;
}

export type KalenderEintrag<T extends KalenderTerminRoh> =
  | { art: 'termin'; id: string; tag: string; kat: TerminKategorie; termin: T }
  | { art: 'frist'; id: string; tag: string; frist: FristEintrag };

/** Einträge je Tag (YYYY-MM-DD): Termine nach Filter, Fristen nur wenn eingeschaltet. Je Tag nach Uhrzeit, Fristen zuletzt. */
export function eintraegeJeTag<T extends KalenderTerminRoh>(
  termine: readonly T[], fristen: readonly FristEintrag[], filter: KalenderFilter, mitFristen: boolean,
): Map<string, KalenderEintrag<T>[]> {
  const m = new Map<string, KalenderEintrag<T>[]>();
  const dazu = (tag: string, e: KalenderEintrag<T>) => m.set(tag, [...(m.get(tag) ?? []), e]);
  [...termine]
    .sort((a, b) => a.start_datum.localeCompare(b.start_datum))
    .forEach(t => {
      const kat = terminKategorie(t);
      if (filter !== 'alle' && kat !== filter) return;
      const tag = terminTag(t.start_datum, t.ganztaegig);
      dazu(tag, { art: 'termin', id: t.id, tag, kat, termin: t });
    });
  if (mitFristen) fristen.forEach(f => dazu(f.datum, { art: 'frist', id: f.id, tag: f.datum, frist: f }));
  return m;
}

/** Anzahl je Filter (für die Chips), Fristen zählen nicht mit. */
export function kategorieAnzahl(termine: readonly KalenderTerminRoh[]): Record<KalenderFilter, number> {
  const z: Record<KalenderFilter, number> = { alle: termine.length, wartung: 0, vermietung: 0, versammlung: 0, ablesung: 0, sonstige: 0 };
  termine.forEach(t => { z[terminKategorie(t)] += 1; });
  return z;
}

/** „Alle Termine aus 8 Objekten · 34 Termine“ */
export function kalenderUntertitel(termine: readonly KalenderTerminRoh[]): string {
  const objekte = new Set(termine.map(t => t.objekt_id).filter(Boolean)).size;
  return `Alle Termine aus ${objekte} ${objekte === 1 ? 'Objekt' : 'Objekten'} · ${termine.length} ${termine.length === 1 ? 'Termin' : 'Termine'}`;
}

/** Monatsgrenzen für die Abfrage: [erster Tag, erster Tag des Folgemonats). */
export function monatsGrenzen(jahr: number, monat: number): { von: string; bis: string } {
  const p = (j: number, m: number) => `${j}-${String(m).padStart(2, '0')}-01`;
  return { von: p(jahr, monat), bis: monat === 12 ? p(jahr + 1, 1) : p(jahr, monat + 1) };
}
